/**
 * The list of keyboard shortcuts (`?`), with the switch to turn them off in this browser.
 */

import { Button, Dialog, Kbd, Switch, Typography } from "@norican/react";
import { useState } from "react";

import { setShortcutsOn, shortcutsOn } from "@/lib/features";

/** One shortcut: the key and what it does. */
export interface ShortcutRow {
  key: string;
  text: string;
}

/**
 * The keys of the case page. The row keys (ease round, 13 Oct 2026) act on the row of the
 * draft that holds the focus: the arrow keys move there, "Go to the draft" starts there.
 */
export const CASE_SHORTCUTS: readonly ShortcutRow[] = [
  { key: "j", text: "Next case" },
  { key: "k", text: "Previous case" },
  { key: "a", text: "Accept the matched items" },
  { key: "c", text: "Confirm the suggested customer" },
  { key: "t", text: "Take the case" },
  { key: "d", text: "Mark done and next" },
  { key: "r", text: "Copy the reply" },
  { key: "↑ ↓", text: "Move in the draft" },
  { key: "Enter", text: "Confirm the row" },
  { key: "n", text: "No item for the row" },
  { key: "p", text: "Set the price of the row" },
  { key: "q", text: "Set the quantity of the row" },
  { key: "i", text: "Pick an item for the row" },
  { key: "?", text: "This list" },
];

export const QUEUE_SHORTCUTS: readonly ShortcutRow[] = [
  { key: "j", text: "Next row" },
  { key: "k", text: "Previous row" },
  { key: "Enter", text: "Open the row" },
  { key: "x", text: "Select the row" },
  { key: "?", text: "This list" },
];

export function ShortcutHelpDialog({
  open,
  onOpenChange,
  rows,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: readonly ShortcutRow[];
}) {
  const [on, setOn] = useState(shortcutsOn);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup>
          <Dialog.Header>
            <Dialog.Title>
              <Typography as="span" variant="heading-6" className="text-fg">
                Keyboard shortcuts
              </Typography>
            </Dialog.Title>
            <Dialog.CloseButton />
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-3">
            <Dialog.Description>
              <Typography as="span" variant="body-sm" tone="muted">
                The keys do nothing while you type in a field.
              </Typography>
            </Dialog.Description>
            <ul className="flex flex-col gap-1.5">
              {rows.map((row) => (
                <li key={row.key} className="flex items-center gap-3">
                  <Kbd size="sm" className="min-w-8 justify-center">
                    {row.key}
                  </Kbd>
                  <span className="text-fg text-sm">{row.text}</span>
                </li>
              ))}
            </ul>
            <Switch.Root
              checked={on}
              onCheckedChange={(checked) => {
                setShortcutsOn(checked);
                setOn(checked);
              }}
              label={<span className="text-fg-muted text-xs">Use keyboard shortcuts</span>}
            />
          </Dialog.Body>
          <Dialog.Footer className="flex justify-end">
            <Button variant="outline" tone="neutral" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
